import { Stack } from '@mui/material';
import { useCallback, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LassieMiniParams, Templates } from '@thive/fddm-types';

import { Accordion } from '@common/components/Accordion';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { WorkspaceMode } from '@/modules/common/types/general';
import { NumberInput } from '@/modules/common/components/inputs';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { LoadingServiceFactory } from '@/modules/app/service/LoadingServiceFactory';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

import { TpsServiceFactory } from '../service/TpsServiceFactory';
import { DisabledReasonTooltip } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/common/DisabledReasonTooltip';
import { UserPreferenceName } from '@/modules/userPreferences';
import { getRegeneratedTemplateId } from '../helpers';
import {
  FIELDS_PADDING_BOTTOM,
  FIELDS_SPACING,
  PANEL_ACCORDION_SX,
} from '@/modules/common/constants/propertyPanel';

interface Props {
  selectedLassieMinis: Templates['lassieMinis'];
}

function LassieMiniTemplateParametersComponent({ selectedLassieMinis }: Props) {
  const { t } = useTranslation();
  const mode = useAppStore((state) => state.mode);
  const loadingSvc = useMemo(() => LoadingServiceFactory.getInstance().createLoadingService(), []);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const tpsSvc = useMemo(() => TpsServiceFactory.getInstance().createTpsService(), []);

  const lassieMiniGroup = selectedLassieMinis[0];
  const newGenerationParams = lassieMiniGroup?.params;

  const regenerate = useCallback(
    async (params?: Partial<LassieMiniParams>) => {
      if (!newGenerationParams || !lassieMiniGroup) return;

      loadingSvc.showLoadContainer('TRANSPARENT');

      const regeneration = await tpsSvc.regenerateLassieMiniTemplate({
        ...lassieMiniGroup,
        params: { ...newGenerationParams, ...params },
      });
      // Regenerating can give the template a new id: keep it selected
      tpsSvc.selectTemplates([getRegeneratedTemplateId(regeneration) ?? lassieMiniGroup.group.id]);

      loadingSvc.hideLoadContainer();
    },
    [newGenerationParams, lassieMiniGroup, tpsSvc, loadingSvc],
  );

  const onNumAislesChange = useCallback(
    (newValue: number) => {
      fddmSvc.setPreference('template.lassieMini.numAisles', newValue);
      regenerate({ numAisles: newValue });
    },
    [fddmSvc, regenerate],
  );

  const onNumBaysChange = useCallback(
    (newValue: number) => {
      fddmSvc.setPreference('template.lassieMini.numBays', newValue);
      regenerate({ numBays: newValue });
    },
    [fddmSvc, regenerate],
  );

  const onNumShelvesChange = useCallback(
    (newValue: number) => {
      fddmSvc.setPreference('template.lassieMini.numShelves', newValue);
      regenerate({ numShelves: newValue });
    },
    [fddmSvc, regenerate],
  );

  const onlyOneSelected = selectedLassieMinis.length === 1;
  const isDisabled = mode !== WorkspaceMode.EDITABLE || !onlyOneSelected;

  if (!newGenerationParams) return null;

  return (
    <Accordion
      persistId={UserPreferenceName.ACCORDION_PROPERTY_PANEL_TEMPLATE_LASSIE_MINI}
      title={t('interface:properties.template_title.lassie_mini', 'Lassie Mini')}
      unmountOnExit
      sx={PANEL_ACCORDION_SX}
    >
      <Stack direction='column' spacing={FIELDS_SPACING} pb={FIELDS_PADDING_BOTTOM}>
        <PropertiesLabel i18nkey='interface:properties.template.aisles'>
          <DisabledReasonTooltip
            reasonKey={
              onlyOneSelected ? undefined : 'interface:properties.area.templates.partial_selection'
            }
          >
            <NumberInput
              value={newGenerationParams.numAisles}
              onChange={onNumAislesChange}
              disabled={isDisabled}
              minValue={1}
            />
          </DisabledReasonTooltip>
        </PropertiesLabel>
        <PropertiesLabel i18nkey='interface:properties.template.bays'>
          {' '}
          <DisabledReasonTooltip
            reasonKey={
              onlyOneSelected ? undefined : 'interface:properties.area.templates.partial_selection'
            }
          >
            <NumberInput
              value={newGenerationParams.numBays}
              onChange={onNumBaysChange}
              disabled={isDisabled}
              minValue={1}
            />
          </DisabledReasonTooltip>
        </PropertiesLabel>
        <PropertiesLabel i18nkey='interface:properties.template.shelves'>
          <DisabledReasonTooltip
            reasonKey={
              onlyOneSelected ? undefined : 'interface:properties.area.templates.partial_selection'
            }
          >
            <NumberInput
              value={newGenerationParams.numShelves}
              onChange={onNumShelvesChange}
              minValue={1}
              disabled={isDisabled}
            />
          </DisabledReasonTooltip>
        </PropertiesLabel>
      </Stack>
    </Accordion>
  );
}

export const LassieMiniTemplateParameters = memo(LassieMiniTemplateParametersComponent);
